/**
 * BarbershopTalk progressive tutorial configuration (v1).
 * Arrival tips (why you are here) are separate from feature tips (what this means).
 */

export const TUTORIAL_VERSION = 1;
export const JOURNEY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const STORAGE_KEYS = {
  version: 'bstTutorialVersion',
  welcomeCompleted: 'bstWelcomeCompleted',
  skipped: 'bstTutorialSkipped',
  interest: 'bstOnboardingInterest',
  completed: 'bstCompletedItems',
  dismissed: 'bstDismissedCoachmarks',
  welcomeSession: 'bstWelcomeShownThisSession',
  tutorialStarted: 'bstTutorialStarted',
  activeItem: 'bstActiveTutorialItem',
  pendingArrival: 'bstPendingTutorialArrival',
  journeyStartedAt: 'bstTutorialJourneyStartedAt',
  launcherDismissedSession: 'bstLauncherDismissedSession',
  resumePath: 'bstTutorialResumePath',
  journeyPath: 'bstTutorialJourneyPath',
  completedPageTours: 'bstCompletedPageTours',
  dismissedPageTourInvitations: 'bstDismissedPageTourInvitations',
  pageHighlightsVersion: 'bstPageHighlightsVersion',
};

/** Bump only when Page Highlights schema must reset completions */
export const PAGE_HIGHLIGHTS_VERSION = 1;

export const INTERESTS = {
  news: 'news',
  sports: 'sports',
  community: 'community',
  all: 'all',
};

export const INTEREST_LABELS = [
  { id: INTERESTS.news, label: 'News and politics' },
  { id: INTERESTS.sports, label: 'Sports intelligence' },
  { id: INTERESTS.community, label: 'Community' },
  { id: INTERESTS.all, label: 'Show me everything' },
];

/** Shown on the checklist so interest choice is visibly consequential */
export const INTEREST_FOCUS = {
  [INTERESTS.news]: {
    label: 'News and politics',
    blurb: 'Your list leads with the Daily Cut, Political Intelligence, and Bias tools.',
    suggestedIds: ['daily-cut', 'political-intelligence', 'bias-barometer'],
  },
  [INTERESTS.sports]: {
    label: 'Sports intelligence',
    blurb: 'Your list leads with Under Sniper and the Daily Cut after Home.',
    suggestedIds: ['under-sniper', 'daily-cut'],
  },
  [INTERESTS.community]: {
    label: 'Community',
    blurb: 'Your list leads with the Daily Cut and alerts, then politics tools you can talk about.',
    suggestedIds: ['daily-cut', 'alerts'],
  },
  [INTERESTS.all]: {
    label: 'A full tour',
    blurb: 'Your list leads with the Daily Cut, then the intelligence and sports products that deepen the product.',
    suggestedIds: ['daily-cut', 'political-intelligence', 'under-sniper'],
  },
};

export const DEFAULT_PRIMARY_ORDER = [
  'home',
  'daily-cut',
  'political-intelligence',
  'bias-barometer',
  'barbershop-take',
  'under-sniper',
  'alerts',
  'compare-coverage',
];

export const INTEREST_ORDER = {
  [INTERESTS.news]: [
    'home',
    'daily-cut',
    'political-intelligence',
    'bias-barometer',
    'barbershop-take',
    'compare-coverage',
    'alerts',
    'under-sniper',
  ],
  [INTERESTS.sports]: [
    'home',
    'under-sniper',
    'daily-cut',
    'alerts',
    'political-intelligence',
    'bias-barometer',
    'barbershop-take',
    'compare-coverage',
  ],
  [INTERESTS.community]: [
    'home',
    'daily-cut',
    'alerts',
    'political-intelligence',
    'compare-coverage',
    'bias-barometer',
    'barbershop-take',
    'under-sniper',
  ],
  [INTERESTS.all]: [...DEFAULT_PRIMARY_ORDER],
};

/**
 * completion: 'feature' = meaningful event anytime after tutorial started
 *             'passive' = only when item is active + arrival shown + destination rendered
 */
export const PRIMARY_ITEMS = [
  {
    id: 'home',
    title: 'Understand your Home feed',
    body: 'Pulse, the Daily Cut, political intelligence, and Under Sniper entry points live here.',
    actionLabel: 'Orient on Home',
    destination: { type: 'route', path: '/' },
    arrivalId: 'arrival-home',
    featureTipId: null,
    completion: 'passive',
    expectedPaths: ['/'],
  },
  {
    id: 'daily-cut',
    title: 'Open the Daily Cut',
    body: 'The flagship briefing across politics, sports, business, and culture. Free to start. The habit that brings people back.',
    actionLabel: 'Open the Daily Cut',
    destination: { type: 'route', path: '/newsletter' },
    arrivalId: 'arrival-daily-cut',
    featureTipId: null,
    completion: 'passive',
    expectedPaths: ['/newsletter'],
  },
  {
    id: 'political-intelligence',
    title: 'Open Political Intelligence',
    body: 'Blindspots and briefing context in one place.',
    actionLabel: 'Open brief',
    destination: { type: 'route', path: '/politics/intelligence' },
    arrivalId: 'arrival-political-intelligence',
    featureTipId: null,
    completion: 'passive',
    expectedPaths: ['/politics/intelligence'],
  },
  {
    id: 'bias-barometer',
    title: 'Read a Bias Barometer',
    body: 'Article lean is separate from the publisher source cohort. Center is not the same as Insufficient Evidence.',
    actionLabel: 'Open a politics article',
    destination: {
      type: 'politics-article',
      fallbackPath: '/politics/left-wing',
      anchor: 'bias-barometer',
    },
    arrivalId: 'arrival-bias-barometer',
    featureTipId: 'bias-barometer',
    completion: 'feature',
    expectedPaths: [
      '/politics/',
      '/politics/left-wing',
      '/politics/right-wing',
      '/politics/center',
    ],
  },
  {
    id: 'barbershop-take',
    title: 'Cast a Barbershop Take',
    body: 'The model gives a reading. The barbershop gives theirs. Voting does not rewrite the model.',
    actionLabel: 'Open a politics article',
    destination: {
      type: 'politics-article',
      fallbackPath: '/politics/left-wing',
      anchor: 'barbershop-take',
    },
    arrivalId: 'arrival-barbershop-take',
    featureTipId: 'barbershop-take',
    completion: 'feature',
    expectedPaths: [
      '/politics/',
      '/politics/left-wing',
      '/politics/right-wing',
      '/politics/center',
    ],
  },
  {
    id: 'under-sniper',
    title: 'Explore The Under Sniper',
    body: 'Statistical player-prop analysis for NBA, NHL, and MMA. Not a guarantee. Published picks are on Limited or Full.',
    actionLabel: 'How a pick makes the board',
    destination: { type: 'route', path: '/under-sniper' },
    arrivalId: 'arrival-under-sniper',
    featureTipId: 'sniper-picks',
    completion: 'passive',
    expectedPaths: ['/under-sniper', '/picks'],
  },
  {
    id: 'alerts',
    title: 'Set your alerts',
    body: 'Watch keywords for free. Sports, quiet hours, and Advanced Topics live in Notification Center.',
    actionLabel: 'Open Notification Center',
    destination: { type: 'route', path: '/notifications' },
    arrivalId: 'arrival-alerts',
    featureTipId: 'notification-center',
    completion: 'passive',
    expectedPaths: ['/notifications'],
    requiresAuth: true,
  },
  {
    id: 'compare-coverage',
    title: 'Compare coverage',
    body: 'See how different publisher cohorts covered the same story.',
    actionLabel: 'Find a multi-source story',
    destination: { type: 'route', path: '/' },
    arrivalId: 'arrival-compare-coverage',
    featureTipId: 'compare-coverage',
    completion: 'feature',
    expectedPaths: ['/', '/politics/'],
  },
];

export const OPTIONAL_ITEMS = [
  {
    id: 'conversation',
    title: 'Join the conversation',
    body: 'Browse The Barbershop for community statuses. Article comments live on article pages. Status posting needs an account.',
    actionLabel: 'Open The Barbershop',
    destination: { type: 'route', path: '/the-barbershop' },
    arrivalId: null,
    featureTipId: null,
  },
  {
    id: 'friends-following',
    title: 'Understand friends and following',
    body: 'Following is one-way. Friendship is mutual. Browse The Barbershop to see community activity.',
    actionLabel: 'Open The Barbershop',
    destination: { type: 'route', path: '/the-barbershop' },
    arrivalId: null,
    featureTipId: 'public-profile',
  },
  {
    id: 'shop',
    title: 'Browse the Shop',
    body: 'Merchandise lives on the external BarbershopTalk store.',
    actionLabel: 'Open store',
    destination: { type: 'external', url: 'https://barbershoptalk.shop' },
    arrivalId: null,
    featureTipId: null,
  },
];

/** Arrival tips: why you are here / what to do next */
export const ARRIVAL_TIPS = {
  'arrival-home': {
    id: 'arrival-home',
    kind: 'arrival',
    title: 'Your Home feed',
    body: 'Home is the hub. Look for the Daily Cut, political intelligence, and Under Sniper. Those are the products that deepen the read and bring people back.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'arrival-daily-cut': {
    id: 'arrival-daily-cut',
    kind: 'arrival',
    title: 'The Daily Cut',
    body: 'This is the flagship briefing: politics, sports, business, and culture in one cut. Scroll the sections. If you do not have an account yet, create a free one to keep the habit.',
    nextAction: 'Next: Skim today\'s cut, then keep exploring.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'arrival-political-intelligence': {
    id: 'arrival-political-intelligence',
    kind: 'arrival',
    title: 'Political Intelligence',
    body: 'This brief surfaces Blindspots and briefing context. Publisher cohort coverage stays separate from any single article Bias Barometer.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'arrival-bias-barometer': {
    id: 'arrival-bias-barometer',
    kind: 'arrival',
    title: 'Bias Barometer',
    body: 'This rating is about how this article presents the issue, not the publisher overall. Center is not the same as Insufficient Evidence.',
    nextAction: 'Next: Read the Bias Barometer on this page.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'arrival-barbershop-take': {
    id: 'arrival-barbershop-take',
    kind: 'arrival',
    title: 'The Barbershop Take',
    body: 'The model gives a reading. The barbershop gives theirs. Cast your vote below. Viewing alone does not finish this step.',
    nextAction: 'Next: Cast your Take on this card.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'arrival-compare-coverage': {
    id: 'arrival-compare-coverage',
    kind: 'arrival',
    title: 'Compare how the story was covered',
    body: 'Open a story with coverage from multiple publisher cohorts, then select Compare Coverage.',
    nextAction: 'Next: Open a multi-source story and compare coverage.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'arrival-alerts': {
    id: 'arrival-alerts',
    kind: 'arrival',
    title: 'Notification Center',
    body: 'Activity covers friend requests and social updates. Keyword alerts are free. Challenge Your View lets you choose which article leans and tones belong in your alerts on eligible plans. It does not rewire your entire feed.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'arrival-conversation': {
    id: 'arrival-conversation',
    kind: 'arrival',
    title: 'The Barbershop',
    body: 'Community statuses live here. Article comments live on article pages. Browsing is open. Posting statuses and replies needs an account.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'arrival-under-sniper': {
    id: 'arrival-under-sniper',
    kind: 'arrival',
    title: 'The Under Sniper',
    body: 'This overview explains how a pick makes the board. The picks desk is at /picks. Limited is a few of today\'s picks. Full is every pick that made the cut. Analysis is entertainment, not a guarantee.',
    primaryLabel: 'Got it',
    secondaryLabel: 'Open picks desk',
    secondaryTo: '/picks',
    showOpenChecklist: true,
  },
};

/** Feature tips: what the mounted feature means */
export const FEATURE_TIPS = {
  'bias-barometer': {
    id: 'bias-barometer',
    kind: 'feature',
    title: 'Bias Barometer',
    body: 'This rating is about how this article presents the issue, not the publisher overall. Bias score is a position on the spectrum, not confidence. Insufficient Evidence is not Center.',
    primaryLabel: 'Got it',
    secondaryLabel: 'How it works',
    secondaryTo: '/how-bias-analysis-works',
    showOpenChecklist: true,
  },
  'barbershop-take': {
    id: 'barbershop-take',
    kind: 'feature',
    title: 'The Barbershop Take',
    body: 'The model gives a reading. The barbershop gives theirs. Your vote stays separate from the model result. Community results unlock after enough readers weigh in.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'notification-center': {
    id: 'notification-center',
    kind: 'feature',
    title: 'Alerts and preferences',
    body: 'Activity, Your Sports, News, and Settings live here. Keywords are free. Challenge Your View on eligible plans lets you pick a topic and choose which article leans belong in your alerts.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'advanced-topics': {
    id: 'advanced-topics',
    kind: 'feature',
    title: 'Set your lean preferences',
    body: 'Challenge Your View here means which article leans and tones you want in alerts: left, center, and/or right. Publisher cohort stays a separate signal. It does not rewire your whole feed. Insufficient Evidence stays outside directional filters.',
    primaryLabel: 'Got it',
    secondaryLabel: 'About Challenge Your View',
    secondaryTo: '/challenge-your-view',
    showOpenChecklist: true,
  },
  'compare-coverage': {
    id: 'compare-coverage',
    kind: 'feature',
    title: 'Compare coverage',
    body: 'This view shows how different publisher cohorts covered the same story. It does not change the Bias Barometer reading on any single article.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'public-profile': {
    id: 'public-profile',
    kind: 'feature',
    title: 'Friends and following',
    body: 'Following lets you keep up with someone\'s public activity. Friendship is a mutual connection that both people accept. Following is one-way and immediate. Friendship uses a request both people accept.',
    primaryLabel: 'Got it',
    showOpenChecklist: true,
  },
  'sniper-picks': {
    id: 'sniper-picks',
    kind: 'feature',
    title: 'The Under Sniper picks',
    body: 'This is statistical analysis for entertainment, not a guarantee. Published picks are on Limited or Full. The Under Sniper page explains how a pick makes the board.',
    primaryLabel: 'Got it',
    secondaryLabel: 'Methodology',
    secondaryTo: '/methodology#under-sniper',
    showOpenChecklist: true,
  },
  'challenge-your-view': {
    id: 'challenge-your-view',
    kind: 'feature',
    title: 'Challenge Your View',
    body: 'This page explains the idea. The controls live in Notification Center: pick a topic, then set which article leans belong in your alerts. It does not control your entire feed.',
    primaryLabel: 'Set alerts',
    secondaryLabel: 'Got it',
    secondaryTo: null,
    primaryTo: '/notifications',
    showOpenChecklist: true,
  },
};

/** Combined lookup for TutorialCoachmark */
export const COACHMARKS = {
  ...ARRIVAL_TIPS,
  ...FEATURE_TIPS,
};

export const WELCOME_SCREENS = [
  {
    id: 1,
    title: 'Welcome to BarbershopTalk',
    kicker: 'More context. Less heat.',
    body: 'See who covered the story, how the article leans, what the tone is doing, and what your usual sources may have missed.',
  },
  {
    id: 2,
    title: 'Bring your side. See the whole room.',
    kicker: null,
    body: 'Compare perspectives, challenge your usual view, and join the conversation without letting one side own it.',
  },
  {
    id: 3,
    title: 'What brings you in?',
    kicker: null,
    body: 'This chooses what the Getting Started list puts first. You can still open every stop.',
    showInterest: true,
  },
];

export function getInterestFocus(interestId) {
  return INTEREST_FOCUS[interestId] || INTEREST_FOCUS[INTERESTS.all];
}

export function getPrimaryItem(id) {
  return PRIMARY_ITEMS.find((item) => item.id === id) || null;
}

export function getOptionalItem(id) {
  return OPTIONAL_ITEMS.find((item) => item.id === id) || null;
}

export function getAllItemIds() {
  return [...PRIMARY_ITEMS.map((i) => i.id), ...OPTIONAL_ITEMS.map((i) => i.id)];
}

export function getAllCoachmarkIds() {
  return Object.keys(COACHMARKS);
}

export function getCoachmark(id) {
  return COACHMARKS[id] || null;
}

export function pathMatchesExpected(path, expectedPaths = []) {
  if (!path || !expectedPaths.length) return false;
  const normalized = path.split('?')[0];
  return expectedPaths.some((expected) => {
    if (expected === '/') return normalized === '/' || normalized === '';
    return normalized === expected || normalized.startsWith(expected);
  });
}

/**
 * Optional Page Highlights (spatial page tours).
 * Separate from arrival tips and feature coachmarks.
 * Phase 1: home, notifications, political-intelligence, under-sniper, public-profile.
 * Steps are filtered at runtime to visible targets only (max 4).
 */
export const PAGE_TOURS = {
  home: {
    id: 'home',
    matchPaths: ['/'],
    exactPath: true,
    invitationTitle: 'Want a quick tour of this page?',
    invitationBody: 'See where the most useful tools and insights are located.',
    steps: [
      {
        id: 'home-feed',
        targetId: 'home-feed',
        title: 'Your feed',
        body: 'This is the main stream of stories. Use the tabs to switch between news, personalized picks, and topics you follow. Must have an account for additional feeds.',
      },
      {
        id: 'home-pulse',
        targetId: 'media-pulse',
        title: 'Media Pulse',
        body: 'A quick read on whether coverage is turning positive, negative, or chaotic across stories people are reading now.',
      },
      {
        id: 'home-compare',
        targetId: 'trending-stories',
        title: 'Compare coverage',
        body: 'When multi-source clusters appear here, open Compare Coverage to see how different publisher cohorts frame the same story.',
        optional: true,
      },
      {
        id: 'home-intel',
        targetId: 'home-intelligence',
        title: 'Flagship entry points',
        body: 'Jump into the Daily Cut, Blindspot Detector, and Under Sniper from this strip without digging through the feed.',
      },
    ],
  },
  notifications: {
    id: 'notifications',
    matchPaths: ['/notifications'],
    invitationTitle: 'Want a quick tour of this page?',
    invitationBody: 'See Activity, Your Sports, News, and Settings.',
    steps: [
      {
        id: 'nc-activity',
        targetId: 'nc-tab-activity',
        title: 'Activity',
        body: 'Friend requests and social updates land here.',
        setTab: 'activity',
      },
      {
        id: 'nc-sports',
        targetId: 'nc-tab-sports',
        title: 'Your Sports',
        body: 'Set how much you want to hear about the teams, players, and fighters you already follow. Under Sniper Picks Ready lives here too.',
        setTab: 'sports',
      },
      {
        id: 'nc-news',
        targetId: 'nc-tab-news',
        title: 'News',
        body: 'Keywords are free. Challenge Your View and Advanced Topics live here.',
        setTab: 'news',
      },
      {
        id: 'nc-settings',
        targetId: 'nc-tab-settings',
        title: 'Settings',
        body: 'Push master, quiet hours, Daily Cut, and From the Barbershop alerts.',
        setTab: 'settings',
      },
      {
        id: 'nc-sniper',
        targetId: 'nc-sniper-panel',
        title: 'Under Sniper',
        body: 'Picks Ready for NBA, NHL, and MMA lives on Your Sports.',
        setTab: 'sports',
        optional: true,
      },
      {
        id: 'nc-cyv',
        targetId: 'nc-cyv-panel',
        title: 'Set your lean preferences',
        body: 'Choose which article leans belong in your alerts: left, center, and/or right. Optionally set tone. Publisher cohort stays a separate signal. Insufficient Evidence stays outside directional filters.',
        setTab: 'news',
        optional: true,
      },
    ],
  },
  'political-intelligence': {
    id: 'political-intelligence',
    matchPaths: ['/politics/intelligence'],
    invitationTitle: 'Want a quick tour of this page?',
    invitationBody: 'See the briefing, Blindspots, and coverage context on this brief.',
    steps: [
      {
        id: 'pi-header',
        targetId: 'brief-title',
        title: 'Political Intelligence brief',
        body: 'Today\'s briefing summary and what the metrics on this page mean.',
      },
      {
        id: 'pi-narrative',
        targetId: 'top-narrative-heading',
        title: 'Top narrative',
        body: 'The story driving attention right now, with momentum and emotional-gap context when available.',
        optional: true,
      },
      {
        id: 'pi-blindspot',
        targetId: 'blindspot-heading',
        title: 'Blindspots',
        body: 'What one side of coverage may be underplaying relative to the rest of the room.',
        optional: true,
      },
      {
        id: 'pi-widget',
        targetId: 'blindspot-widget',
        title: 'Blindspot tools',
        body: 'Deeper Blindspot surfaces live here. Publisher cohort coverage stays separate from any single-article Bias Barometer.',
        optional: true,
      },
    ],
  },
  'under-sniper': {
    id: 'under-sniper',
    matchPaths: ['/under-sniper'],
    invitationTitle: 'Want a quick tour of this page?',
    invitationBody: 'See how The Under Sniper works and where the full picks dashboard lives.',
    steps: [
      {
        id: 'us-hero',
        targetId: 'sniper-h1',
        title: 'The Under Sniper',
        body: 'The Under Sniper publishes picks only when they clear the bar. Live picks are logged before the event and graded in public.',
      },
      {
        id: 'us-what',
        targetId: 'what-heading',
        title: 'What it is',
        body: 'A sport-specific model. Nothing publishes unless it clears the bar. No forced picks.',
      },
      {
        id: 'us-how',
        targetId: 'how-it-works',
        title: 'How a pick makes the board',
        body: 'How we publish, and how live results stay on the record.',
      },
      {
        id: 'us-access',
        targetId: 'sniper-access-cta',
        title: 'Access',
        body: 'The story page is open to everyone. The picks desk is at /picks. Limited is a few of today\'s picks. Full is the whole board.',
      },
    ],
  },
  'public-profile': {
    id: 'public-profile',
    matchPaths: ['/profiles/'],
    invitationTitle: 'Want a quick tour of this page?',
    invitationBody: 'See how following and friendship work on someone\'s profile.',
    steps: [
      {
        id: 'pp-header',
        targetId: 'profile-header',
        title: 'Public profile',
        body: 'Username, photo, and community stats for this person.',
      },
      {
        id: 'pp-follow',
        targetId: 'profile-follow-action',
        title: 'Following',
        body: 'Following is one-way and immediate. You can keep up with their public activity without waiting for approval.',
        optional: true,
      },
      {
        id: 'pp-friend',
        targetId: 'profile-friend-action',
        title: 'Friendship',
        body: 'Friendship is mutual. It uses a request that both people accept.',
        optional: true,
      },
      {
        id: 'pp-statuses',
        targetId: 'profile-statuses',
        title: 'Their statuses',
        body: 'Public community statuses from this person appear here.',
        optional: true,
      },
    ],
  },
};

export function getPageTour(id) {
  return PAGE_TOURS[id] || null;
}

export function resolvePageTourIdForPath(path) {
  const normalized = (path || '').split('?')[0];
  if (!normalized) return null;
  if (normalized === '/' || normalized === '') return 'home';
  if (normalized === '/notifications' || normalized.startsWith('/notifications')) {
    return 'notifications';
  }
  if (normalized === '/politics/intelligence' || normalized.startsWith('/politics/intelligence')) {
    return 'political-intelligence';
  }
  if (normalized === '/under-sniper' || normalized.startsWith('/under-sniper')) {
    return 'under-sniper';
  }
  if (normalized.startsWith('/profiles/')) {
    return 'public-profile';
  }
  return null;
}
